import { Avatar } from "@dash/avatar";
import { RowCellContent, Table, TableBody, TableHeadColumn } from "@dash/table";
import { useTheme } from "@emotion/react";
import Decimal from "decimal.js";

import { Region } from "@megaron/crm-contracts";
import { UserDoc } from "@megaron/docs-contracts";

import { GoToVisitsButton } from "./GoToVisitsButton";
import { RealizationProgressBar } from "./RealizationProgressBar";
import { getMegaraxPath } from "@megaron/megarax-uri";

export type ExtendedRegion = {
  region: Region;
  profitMetrics: {
    profit?: Decimal;
    target?: Decimal;
  };
  profile?: UserDoc;
  legacyUserId?: number;
};

type Props = {
  regions: ExtendedRegion[];
  isLoading: boolean;
};

export const RegionsTable: React.FC<Props> = ({ regions, isLoading }) => {
  const theme = useTheme();

  const tableColumns: TableHeadColumn<"">[] = [
    ...tableHeadingsList.map(
      ({ heading }) =>
        ({
          isSortable: false,
          label: heading,
        }) as const,
    ),
  ];

  const getTableRowCellsContent = (region: ExtendedRegion): RowCellContent[] => [
    {
      isLink: true,
      cellCss: {
        color: theme.colors.primary,
        fontWeight: 600,
        minWidth: "8rem",
      },
      element: region.region.name,
    },
    {
      isLink: true,
      cellCss: {
        color: theme.colors.primary,
        minWidth: "12rem",
      },
      element: (
        <div css={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
          <Avatar src={region.profile ? region.profile.profilePictureUrl : null} />
          <span css={{ color: "black" }}>
            {region.profile?.firstName} {region.profile?.lastName}
          </span>
        </div>
      ),
    },
    {
      isLink: true,
      element: region.region.customerCount,
    },
    {
      isLink: true,
      cellCss: { minWidth: "15rem" },
      element:
        region.profitMetrics.profit && region.profitMetrics.target ? (
          <RealizationProgressBar
            currentRealization={region.profitMetrics.profit?.toNumber()}
            goalRealization={region.profitMetrics.target?.toNumber()}
          />
        ) : (
          <span css={{ color: theme.colors.secondaryText, textAlign: "right", marginLeft: "auto" }}>Brak</span>
        ),
    },
    {
      element: region.legacyUserId && (
        <GoToVisitsButton legacyUserId={region.legacyUserId} css={{ marginLeft: "auto" }} />
      ),
    },
  ];

  return (
    <div css={{ overflow: "auto", margin: "0 -2rem 1rem" }}>
      <Table<""> columns={tableColumns} css={{ padding: "0 2rem" }}>
        <TableBody
          isLoading={isLoading}
          rows={regions.map((region) => ({
            uuid: region.region.name,
            linkTo: getMegaraxPath.uri("megarax://crm/regions/id/{id}", { id: region.region.name }),
            cellsContent: getTableRowCellsContent(region),
            css: {
              "&:hover": {
                "td:nth-child(1)": {
                  textDecoration: "underline",
                },
              },
            },
          }))}
        />
      </Table>
    </div>
  );
};

export const tableHeadingsList: { heading: string }[] = [
  { heading: "Nazwa" },
  { heading: "Handlowiec" },
  { heading: "Klienci" },
  { heading: "Realizacja planu marży" },
  { heading: "" },
];
